import type { ClassId } from '../schemas/classId';
import type { MoveRef } from '../schemas/character';
import type { SpellAccess } from '../schemas/spell';
import type { TradizioneSacraChierico, TradizioneSacraPaladino } from '../schemas/tradizione';
import { expandStartingMoveRefsWithBundles } from './bundle';

/**
 * Modalità di selezione iniziale degli incantesimi per una specifica
 * spell-list attiva del PG.
 *
 * **FI-38 Opz. C (2026-05-14)** — Modello uniformato: il **trigger
 * tecnico del casting** è una mossa `lanciare-incantesimi-X` (precondizione
 * "può lanciare"). Il **mode** della spell-list è poi discriminato dalla
 * mossa-padre del PG (Divinità / Divinità della Natura / Ordine Sacro /
 * Vincolo del Tomo / Ordine Sovrannaturale+choice Tomo). Pattern simmetrico
 * a Mago/Chierico (già pre-FI-38), esteso a Druido/Paladino/Warlock.
 *
 * **Le 2 mosse di lancio uniche del catalogo DWR sono**:
 *  - `mago.lanciare-incantesimi-arcani` → `mago-native` (lista Mago)
 *  - `chierico.lanciare-incantesimi-divini` → `chierico-native` /
 *    `druido-native` / `paladino-native` (in base a mossa-padre)
 *  - `warlock.lanciare-incantesimi-del-patrono` → `warlock-native`
 *
 * Druido e Paladino-Sacro acquisiscono **letteralmente** la mossa Chierico
 * `lanciare-incantesimi-divini` via bundle cross-class (manuale rr. 3103
 * + 4960: «Ottieni le mosse del Chierico…»). Roll +SAG ereditato dalla
 * mossa Chierico (decisione C1, 2026-05-14).
 *
 * - `'mago-native'`: PG con `mago.lanciare-incantesimi-arcani`. Regola:
 *   3 trucchetti auto + scegli 3 di 8 spell L1.
 * - `'chierico-native'`: PG con `chierico.lanciare-incantesimi-divini`
 *   + `chierico.divinita` (Tradizione da `selectedChoiceGroups['tradizione-sacra'][0]`).
 *   Regola: tutti gli spell L0+L1 della Tradizione scelta auto-aggiunti.
 * - `'druido-native'`: PG con `chierico.lanciare-incantesimi-divini`
 *   + `druido.divinita-della-natura`. Tradizione "Natura" implicita.
 *   Regola: tutti gli spell L0+L1 della Tradizione della Natura auto-aggiunti.
 * - `'paladino-native'`: PG con `chierico.lanciare-incantesimi-divini`
 *   + `paladino.ordine-sacro` (Tradizione da `selectedChoices[0]`).
 *   La Tradizione Natura richiede archetipo Elfo (FI-2 via `SpellAccess.requiresArchetype`).
 *   Regola: tutti gli spell L0+L1 della Tradizione scelta auto-aggiunti.
 * - `'warlock-native'`: PG con `warlock.lanciare-incantesimi-del-patrono`
 *   (acquisita via bundle del Vincolo del Tomo per Warlock vero, o via
 *   bundle propagato da Vincolo del Tomo concesso da Ordine Sovrannaturale
 *   per Paladino-Sovrannaturale-Tomo). Niente Tradizioni (lista del Patrono unica).
 *   Regola: tutte le Suppliche L0 auto + scegli 2 di N spell L1.
 */
export type SpellListMode =
  | 'mago-native'
  | 'chierico-native'
  | 'druido-native'
  | 'paladino-native'
  | 'warlock-native';

/**
 * Una spell-list attiva del PG: la coppia (classe-source, modalità di
 * selezione) determina quali spell sono accessibili e con quale regola di
 * scelta iniziale. Per le classi con tradizioni (Chierico, futuro
 * Druido/Paladino) la `tradizione` del PG è inclusa nella entry.
 *
 * **Liste multiple sullo stesso PG** (caso futuro): un Chierico che
 * multiclassa Mago avrà *due* spell-list attive — una `chierico-native`
 * con regola "tutti della Tradizione" + una `mago-native` con regola
 * "scegli 3 di L1 + cantrip auto". La UI deve renderizzare entrambe come
 * sotto-sezioni indipendenti.
 */
export type ActiveSpellList =
  | { classId: 'mago'; mode: 'mago-native' }
  | { classId: 'chierico'; mode: 'chierico-native'; tradizione: TradizioneSacraChierico }
  | { classId: 'druido'; mode: 'druido-native' }
  | { classId: 'paladino'; mode: 'paladino-native'; tradizione: TradizioneSacraPaladino }
  | { classId: 'warlock'; mode: 'warlock-native' };

/**
 * **FI-38 Opz. C**: ID delle 3 mosse di lancio uniche del catalogo DWR.
 * Quando il PG ne ha una tra `Character.moves`, viene attivata una
 * spell-list — il `mode` specifico è poi discriminato dalla mossa-padre
 * presente nel PG (vedi `discriminateChiericoLineMode` per *LI Divini*).
 *
 * Esposti per riuso esterno (es. `spellPoolDisplay.ts` per discriminare
 * il pool calcolato per la grant LIDV).
 */
export const MAGO_CASTING_MOVE = 'mago.lanciare-incantesimi-arcani';
export const CHIERICO_CASTING_MOVE = 'chierico.lanciare-incantesimi-divini';
export const WARLOCK_CASTING_MOVE = 'warlock.lanciare-incantesimi-del-patrono';

/**
 * Mosse-padre (configurazioni del culto/tomo/ordine) che discriminano il
 * `mode` quando il PG ha `chierico.lanciare-incantesimi-divini`. Esattamente
 * una di queste è presente per un PG casteggiante divino:
 *  - `chierico.divinita` → mode `chierico-native` (Tradizione da choiceGroups)
 *  - `druido.divinita-della-natura` → mode `druido-native` (Tradizione Natura implicita)
 *  - `paladino.ordine-sacro` → mode `paladino-native` (Tradizione da selectedChoices)
 */
const CHIERICO_DIVINITA_MOVE = 'chierico.divinita';
const DRUIDO_DIVINITA_DELLA_NATURA_MOVE = 'druido.divinita-della-natura';
const PALADINO_ORDINE_SACRO_MOVE = 'paladino.ordine-sacro';

const VALID_CHIERICO_TRADIZIONI: ReadonlySet<TradizioneSacraChierico> = new Set([
  'crepuscolo',
  'guarigione',
  'illuminazione',
  'negromanzia',
  'valore',
]);

const VALID_PALADINO_TRADIZIONI: ReadonlySet<TradizioneSacraPaladino> = new Set([
  'negromanzia',
  'valore',
  'natura',
]);

function isValidChiericoTradizione(value: string): value is TradizioneSacraChierico {
  return (VALID_CHIERICO_TRADIZIONI as ReadonlySet<string>).has(value);
}

function isValidPaladinoTradizione(value: string): value is TradizioneSacraPaladino {
  return (VALID_PALADINO_TRADIZIONI as ReadonlySet<string>).has(value);
}

/**
 * Determina quali spell-list sono attive per il PG, in base alle mosse di
 * lancio (`lanciare-incantesimi-X`) che ha tra le sue mosse selezionate.
 *
 * **FI-38 Opz. C (2026-05-14)**: il trigger primario è ora la **mossa di
 * lancio** (`chierico.lanciare-incantesimi-divini` o
 * `warlock.lanciare-incantesimi-del-patrono` o
 * `mago.lanciare-incantesimi-arcani`). Per la linea Divina (Chierico/Druido/
 * Paladino-Sacro) il `mode` è discriminato dalla mossa-padre presente nel
 * PG. Pattern uniforme con Mago/Warlock (dove la mossa-padre non discrimina).
 *
 * **Input ridotto a `moves`** (anziché l'intero `CharacterDraft`) per
 * disaccoppiare il servizio dal tipo specifico della feature builder.
 *
 * Restituisce `[]` se il PG non ha mosse di lancio (es. Bardo L1 senza
 * multiclasse, o Druido che ha scelto *Mutaforma* — il bundle del Druido
 * vive su *Divinità della Natura*, quindi il PG non avrebbe la mossa di
 * lancio) o se ha la mossa di lancio ma non ha completato la configurazione
 * (es. Chierico senza Tradizione scelta).
 */
export function getActiveSpellLists(moves: readonly MoveRef[]): ActiveSpellList[] {
  // **FI-60**: durante l'editing builder L1 il `draft.moves` contiene solo le
  // mosse anchor pickate dal giocatore (es. `paladino.ordine-sacro`), NON
  // i loro bundle members cross-class (es. `chierico.lanciare-incantesimi-divini`).
  // Espandiamo idempotentemente i bundle members prima di scansionare le
  // mosse di lancio. Post-finalize (`Character.moves`) i bundle sono già
  // presenti → expansion no-op.
  const effectiveMoves = expandStartingMoveRefsWithBundles(moves);
  const lists: ActiveSpellList[] = [];
  const seen = new Set<string>();

  function addOnce(list: ActiveSpellList): void {
    const key =
      list.classId === 'chierico' || list.classId === 'paladino'
        ? `${list.classId}:${list.mode}:${list.tradizione}`
        : `${list.classId}:${list.mode}`;
    if (seen.has(key)) return;
    seen.add(key);
    lists.push(list);
  }

  for (const moveRef of effectiveMoves) {
    if (moveRef.id === MAGO_CASTING_MOVE) {
      addOnce({ classId: 'mago', mode: 'mago-native' });
      continue;
    }
    if (moveRef.id === CHIERICO_CASTING_MOVE) {
      // FI-38 Opz. C: discrimina chierico/druido/paladino-native dalla
      // mossa-padre presente nel PG.
      const lineList = discriminateChiericoLineMode(effectiveMoves);
      if (lineList) addOnce(lineList);
      continue;
    }
    if (moveRef.id === WARLOCK_CASTING_MOVE) {
      addOnce({ classId: 'warlock', mode: 'warlock-native' });
      continue;
    }
  }
  return lists;
}

/**
 * **FI-38 Opz. C**: dato un PG che possiede `chierico.lanciare-incantesimi-divini`,
 * discrimina il `mode` della spell-list in base alla mossa-padre presente nel PG:
 *  - `chierico.divinita` → `chierico-native` con Tradizione da choiceGroups
 *  - `druido.divinita-della-natura` → `druido-native` (Tradizione Natura implicita)
 *  - `paladino.ordine-sacro` → `paladino-native` con Tradizione da selectedChoices
 *
 * Esattamente una mossa-padre è presente per un casteggiante divino valido
 * (le 3 mosse-padre sono mutuamente esclusive per costruzione del manuale:
 *  Chierico ha Divinità, Druido sceglie Divinità della Natura tra le starting
 *  choice, Paladino sceglie Ordine Sacro tra le starting choice). Per casi
 *  edge (più mosse-padre presenti — multiclasse esotico futuro) la priorità
 *  è Chierico > Druido > Paladino (la più specifica vince).
 *
 * Restituisce `undefined` se nessuna mossa-padre è presente (caso edge:
 * il PG ha *LI Divini* ma manca la mossa-padre — anomalia di seed).
 */
function discriminateChiericoLineMode(moves: readonly MoveRef[]): ActiveSpellList | undefined {
  const divinita = moves.find((m) => m.id === CHIERICO_DIVINITA_MOVE);
  if (divinita) {
    const tradizione = findChiericoTradizione(moves);
    if (tradizione) return { classId: 'chierico', mode: 'chierico-native', tradizione };
    return undefined;
  }
  const druidoNatura = moves.find((m) => m.id === DRUIDO_DIVINITA_DELLA_NATURA_MOVE);
  if (druidoNatura) {
    return { classId: 'druido', mode: 'druido-native' };
  }
  const paladinoSacro = moves.find((m) => m.id === PALADINO_ORDINE_SACRO_MOVE);
  if (paladinoSacro) {
    const tradizione = findPaladinoTradizione(paladinoSacro);
    if (tradizione) return { classId: 'paladino', mode: 'paladino-native', tradizione };
    return undefined;
  }
  return undefined;
}

/**
 * Estrae la Tradizione Sacra scelta dal PG dalla mossa `chierico.divinita`.
 *
 * **FI-18 Fase 2**: la Tradizione vive in
 * `MoveRef.selectedChoiceGroups['tradizione-sacra'][0]` (mossa-config con
 * 3 `choiceGroups`: tradizione-sacra/sfera/fondamento). Per draft v2
 * pre-Fase-2 (con `choices` shorthand) c'è fallback su
 * `MoveRef.selectedChoices[0]`.
 *
 * Restituisce `undefined` se la mossa non è in `moves` (caso runtime
 * raro: il Chierico la ha sempre come iniziale auto), se nessuna delle 2
 * fonti contiene un valore (Tradizione non ancora scelta), o se il valore
 * non è una delle 5 Tradizioni valide (sanity check sul dato grezzo).
 */
function findChiericoTradizione(moves: readonly MoveRef[]): TradizioneSacraChierico | undefined {
  const divinita = moves.find((m) => m.id === CHIERICO_DIVINITA_MOVE);
  if (!divinita) return undefined;
  const value =
    divinita.selectedChoiceGroups?.['tradizione-sacra']?.[0] ?? divinita.selectedChoices[0];
  return value !== undefined && isValidChiericoTradizione(value) ? value : undefined;
}

/**
 * Estrae la Tradizione Sacra scelta dal Paladino dalla mossa
 * `paladino.ordine-sacro`. La Tradizione vive in `selectedChoices[0]`
 * (mossa-config con `choices` per le 3 Tradizioni: Negromanzia/Valore/
 * Natura).
 *
 * Restituisce `undefined` se le selectedChoices sono vuote (Tradizione non
 * ancora scelta) o se il valore non è una delle 3 Tradizioni valide.
 *
 * **NB sull'archetipo Elfo per la Tradizione Natura**: il vincolo è
 * codificato a livello di `SpellAccess.requiresArchetype: 'elfo'` sui spell
 * della Tradizione Natura del Paladino — la verifica avviene downstream
 * (predicate spells del reducer e UI), non qui. Questa funzione restituisce
 * la Tradizione scelta dal giocatore tale e quale, anche se "Natura" senza
 * archetipo Elfo (caso che il builder dovrebbe prevenire via UI/validazione).
 */
function findPaladinoTradizione(moveRef: MoveRef): TradizioneSacraPaladino | undefined {
  if (moveRef.selectedChoices.length === 0) return undefined;
  const value = moveRef.selectedChoices[0];
  return value !== undefined && isValidPaladinoTradizione(value) ? value : undefined;
}

/**
 * Helper per leggere il `classId` di una spell-list attiva.
 * Esposto come funzione per estensibilità futura (es. per consumare le
 * spell-list senza importare il tipo union dall'esterno).
 */
export function getSpellListClassId(list: ActiveSpellList): ClassId {
  return list.classId;
}

/**
 * **D9 — FI-24**: predicate centralizzato che valuta se una entry
 * `SpellAccess` è disponibile dato lo stato del PG (archetipo scelto +
 * mosse possedute). Combina i 2 vincoli opzionali `requiresArchetype`
 * (FI-2 / FI-7) e `requiresMove` (FI-24) in un unico punto SSOT — i
 * consumer (predicate builder, spell-grants service) lo chiamano invece di
 * duplicare la logica.
 *
 * Ritorna `true` se la entry è accessibile, `false` se almeno un vincolo
 * non è soddisfatto. Vincoli non specificati sull'access (entry "libera")
 * → sempre disponibile.
 *
 * **Pattern SSOT**: prima di D9 i consumer (`isSpellListComplete` in
 * `reducer.ts`) replicavano il check `requiresArchetype` con una `find`+
 * type guard ad hoc. Adesso 1 sola implementazione.
 */
export function isSpellAccessAvailable(
  access: SpellAccess,
  options: {
    archetypeId?: string | undefined;
    ownedMoveIds?: readonly string[] | undefined;
  },
): boolean {
  if ('requiresArchetype' in access && access.requiresArchetype !== undefined) {
    if (options.archetypeId !== access.requiresArchetype) return false;
  }
  if (access.requiresMove !== undefined) {
    if (!options.ownedMoveIds?.includes(access.requiresMove)) return false;
  }
  return true;
}
